import React, { useMemo, useState } from 'react';
import { Table, Button, Popconfirm, Input, Space } from 'antd';
import { useExpense } from '../../store/expenseContext';
import { Expense } from '../../types/expense';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from "react-i18next";
import PageNavigation from "../Layout/PageNavigation";

interface Props {
    onEdit: (expense: Expense) => void;
}

const ExpenseList: React.FC<Props> = ({ onEdit }) => {
    const { expenses, deleteExpense } = useExpense();
    const { t } = useTranslation();
    const [search, setSearch] = useState<string>('');

    const filtered = useMemo(() => {
        return expenses.filter(r =>
            (r.expenseNumber ?? '').toLowerCase().includes(search.toLowerCase()) ||
            (r.category ?? '').toLowerCase().includes(search.toLowerCase()) ||
            (r.paidTo ?? '').toLowerCase().includes(search.toLowerCase())
        );
    }, [expenses, search]);

    const columns = [
        { title: t('expenseNumber'), dataIndex: 'expenseNumber' },
        { title: t('date'), dataIndex: 'date', render: (d: string) => new Date(d).toLocaleDateString() },
        { title: t('category'), dataIndex: 'category' },
        { title: t('amount'), dataIndex: 'amount' },
        { title: t('currency'), dataIndex: 'currency' },
        { title: t('paymentMethod'), dataIndex: 'paymentMethod' },
        { title: t('paidTo'), dataIndex: 'paidTo' },
        { title: t('reference'), dataIndex: 'reference' },
        { title: t('description'), dataIndex: 'description' },
        {
            title: t('actions'),
            render: (_: any, record: Expense) => (
                <Space>
                    <Button icon={<EditOutlined />} onClick={() => onEdit(record)}>{t('edit')}</Button>
                    <Popconfirm
                        title={t('sure_to_delete') as string}
                        onConfirm={() => deleteExpense(record.id!)}
                    >
                        <Button danger icon={<DeleteOutlined />}>{t('delete')}</Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div>
            <PageNavigation title={t('expense_list') as string} />
            <Input.Search
                allowClear
                style={{ marginBottom: 16, maxWidth: 300 }}
                placeholder={t('search_expenses') as string}
                onChange={e => setSearch(e.target.value)}
                value={search}
            />
            <Table
                rowKey={record => record.id as string}
                dataSource={filtered}
                columns={columns}
                pagination={{ pageSize: 8 }}
                scroll={{ x: 'max-content' }}
            />
        </div>
    );
};

export default ExpenseList;
